import { useHandler } from "@redotech/react-util/hook";
import {
  EWWidgetTextProps,
  NO_COVERAGE_TIME_ID,
  Offering,
} from "@redotech/redo-model/extended-warranty";
import { ExtendedWarrantyCartToggleExperience as ExtendedWarrantyCartToggleExperienceEnum } from "@redotech/redo-model/extended-warranty-enable-criteria";
import { memo, useState } from "react";
import { ExtendedWarrantyCheckbox } from "../checkbox";
import { ExtendedWarrantyModal } from "../modal";
import { ExtendedWarrantyCartToggleExperience } from "./base-experience";
import { ExtendedWarrantyCartToggleModalExperience } from "./modal-experience";

export const ExtendedWarrantyCartToggleContent = memo(
  function ExtendedWarrantyCartToggleContent({
    addWarranty,
    removeWarranty,
    variantId,
    selection,
    offerings,
    cartToggleExperience,
    customCss,
    customModalCss,
    logo,
    sideImage,
    text,
    isPreview,
    foundSelector,
  }: {
    addWarranty: (variantId: string, selectedOffering: Offering) => void;
    removeWarranty: (variantId: string) => void;
    variantId: string;
    selection?: Offering;
    offerings: Offering[];
    cartToggleExperience?: ExtendedWarrantyCartToggleExperienceEnum;
    customCss?: string;
    customModalCss?: string;
    logo?: string;
    sideImage?: string;
    text: EWWidgetTextProps;
    isPreview?: boolean;
    foundSelector?: string;
  }) {
    return (
      <>
        {customCss && <style>{customCss}</style>}
        {foundSelector && <meta found-selector={foundSelector} />}
        {cartToggleExperience ===
        ExtendedWarrantyCartToggleExperienceEnum.MODAL ? (
          <ExtendedWarrantyCartToggleModalExperience
            addWarranty={addWarranty}
            customModalCss={customModalCss}
            foundSelector={foundSelector}
            isPreview={isPreview}
            logo={logo}
            offerings={offerings}
            removeWarranty={removeWarranty}
            selection={selection}
            sideImage={sideImage}
            text={text}
            variantId={variantId}
          />
        ) : cartToggleExperience ===
          ExtendedWarrantyCartToggleExperienceEnum.CHECKBOX ? (
          <ExtendedWarrantyCartToggleCheckboxExperience
            addWarranty={addWarranty}
            customCss={customCss}
            customModalCss={customModalCss}
            foundSelector={foundSelector}
            logo={logo}
            offerings={offerings}
            removeWarranty={removeWarranty}
            selection={selection}
            sideImage={sideImage}
            text={text}
            variantId={variantId}
          />
        ) : (
          <ExtendedWarrantyCartToggleExperience
            addWarranty={addWarranty}
            cartToggleExperience={cartToggleExperience}
            offerings={offerings}
            removeWarranty={removeWarranty}
            selection={selection}
            text={text}
            variantId={variantId}
          />
        )}
      </>
    );
  },
);

const ExtendedWarrantyCartToggleCheckboxExperience = memo(
  function ExtendedWarrantyCartToggleCheckboxExperience({
    addWarranty,
    removeWarranty,
    variantId,
    selection,
    offerings,
    customCss,
    customModalCss,
    logo,
    sideImage,
    text,
    foundSelector,
  }: {
    addWarranty: (variantId: string, selectedOffering: Offering) => void;
    removeWarranty: (variantId: string) => void;
    variantId: string;
    selection?: Offering;
    offerings: Offering[];
    customCss?: string;
    customModalCss?: string;
    logo?: string;
    sideImage?: string;
    text: EWWidgetTextProps;
    foundSelector?: string;
  }) {
    const [showModal, setShowModal] = useState(false);

    const handleTileClick = useHandler((offering: Offering) => {
      if (offering.id === NO_COVERAGE_TIME_ID) {
        removeWarranty(variantId);
      } else {
        addWarranty(variantId, offering);
      }
    });

    const handleOpenModal = useHandler(() => {
      setShowModal(true);
    });

    const handleCloseModal = useHandler(() => {
      setShowModal(false);
    });

    return (
      <>
        {customCss && <style>{customCss}</style>}
        {foundSelector && <meta found-selector={foundSelector} />}
        <ExtendedWarrantyCheckbox
          offerings={offerings}
          onSeeDetailsClick={handleOpenModal}
          onTileClick={handleTileClick}
          selectedOffering={selection}
          text={text}
        />
        <ExtendedWarrantyModal
          customCss={customModalCss}
          logo={logo}
          offerings={offerings}
          onClose={handleCloseModal}
          onTileClick={handleTileClick}
          open={showModal}
          selectedOffering={selection}
          sideImage={sideImage}
          text={text}
        />
      </>
    );
  },
);
